// ─── User-facing error copy ───────────────────────────────────────────────────
// Screens print `error.message`, so the message on every error this client
// throws must be fit to show a person. The server's own text is kept on
// `ApiError.serverMessage` for logs and admin tooling.

export const ERROR_COPY = {
  network: "Couldn't reach Atlas. Check your connection and try again in a moment.",
  timeout: "That took too long. Check your connection and try again.",
  server: "Something went wrong on our side. Try again in a moment.",
  rateLimited: "Too many attempts. Wait a few minutes and try again.",
  signedOut: "You've been signed out. Sign in again to carry on.",
  forbidden: "You don't have access to that.",
  notFound: "We couldn't find that.",
  generic: "That didn't work. Try again.",
} as const;

/** Bare status words and machine codes the API uses where no one wrote copy. */
const STOCK_MESSAGES: Record<string, string> = {
  unauthorized: ERROR_COPY.signedOut,
  unauthorised: ERROR_COPY.signedOut,
  forbidden: ERROR_COPY.forbidden,
  "not found": ERROR_COPY.notFound,
  "too many requests": ERROR_COPY.rateLimited,
  "request failed": ERROR_COPY.generic,
  "invalid json body": ERROR_COPY.generic,
  "internal server error": ERROR_COPY.server,
};

function rateLimitedCopy(retryAfterSeconds: number | undefined): string {
  if (!retryAfterSeconds || retryAfterSeconds <= 0) return ERROR_COPY.rateLimited;
  if (retryAfterSeconds < 90) return "Too many attempts. Try again in a minute.";
  const minutes = Math.ceil(retryAfterSeconds / 60);
  return `Too many attempts. Try again in about ${minutes} minutes.`;
}

/**
 * Turn an API error response into a message a person can read. 4xx text is
 * hand-written in the routes and passes through; a 500 is an unhandled
 * exception (SQL errors and the like) and never does.
 */
export function friendlyApiMessage(
  status: number,
  serverMessage: string | undefined,
  retryAfterSeconds?: number,
): string {
  const raw = serverMessage?.trim() ?? "";
  const stock = STOCK_MESSAGES[raw.toLowerCase()];
  if (status === 429) {
    return raw && !stock ? raw : rateLimitedCopy(retryAfterSeconds);
  }
  if (status === 500) return ERROR_COPY.server;
  if (stock) return stock;
  // Machine codes ("verify_email") and missing text.
  if (!raw || /^[a-z0-9]+(_[a-z0-9]+)+$/.test(raw)) {
    if (status >= 500) return ERROR_COPY.server;
    if (status === 401) return ERROR_COPY.signedOut;
    if (status === 403) return ERROR_COPY.forbidden;
    if (status === 404) return ERROR_COPY.notFound;
    return ERROR_COPY.generic;
  }
  // 502/503 carry deliberate copy ("Strava connections are temporarily
  // full"), except the "X not configured" ops messages.
  if (status >= 500 && /not configured/i.test(raw)) return ERROR_COPY.server;
  return raw;
}

export class ApiError extends Error {
  /** The server's own error text, unedited. Not for display. */
  public readonly serverMessage: string;

  constructor(
    public readonly status: number,
    message: string,
    /** Parsed JSON error body, when the response had one. Carries the 402
     *  gate flags (`upgradeRequired`, `historyLocked`) the clients key off. */
    public readonly body?: Record<string, unknown>,
    retryAfterSeconds?: number,
  ) {
    super(friendlyApiMessage(status, message, retryAfterSeconds));
    this.name = "ApiError";
    this.serverMessage = message;
  }

  get upgradeRequired(): boolean {
    return this.body?.["upgradeRequired"] === true;
  }

  get historyLocked(): boolean {
    return this.body?.["historyLocked"] === true;
  }
}

/**
 * Thrown when the request never produced an HTTP response: no connectivity,
 * DNS failure, or the per-request timeout fired. Distinct from ApiError so
 * callers can tell "the server said no" (401/402/404) from "we couldn't reach
 * the server" - the mobile auth layer must never log a user out for the latter.
 */
export class NetworkError extends Error {
  constructor(
    message: string,
    public readonly timedOut: boolean,
    public readonly cause?: unknown,
  ) {
    super(message);
    this.name = "NetworkError";
  }
}

export function isNetworkError(err: unknown): err is NetworkError {
  return err instanceof NetworkError;
}

/**
 * The message to show for a caught error. Errors from the API client already
 * carry readable copy; anything else (a TypeError, a native module's error
 * string) gets the caller's fallback instead of its raw text.
 */
export function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError || err instanceof NetworkError) return err.message;
  return fallback;
}

export function retryAfterSeconds(res: Response): number | undefined {
  const value = Number(res.headers.get("Retry-After"));
  return Number.isFinite(value) && value > 0 ? value : undefined;
}

export interface ApiClientConfig {
  baseUrl: string;
  credentials?: RequestCredentials;
  getAuthHeader?: () => Promise<string | null>;
  /** Abort requests that produce no response within this many ms. Unset = no timeout. */
  timeoutMs?: number;
  /**
   * Extra headers stamped on every request (spec 14 usage analytics):
   * X-Atlas-Client / X-Atlas-App-Version identify the client for session
   * presence, and X-Atlas-Background: 1 tells requireAuth this request came
   * from a background sync (HealthKit, outbox, hidden tab) so it must not
   * count as the user being "in the app". Called per request so it can read
   * live app state.
   */
  getClientHeaders?: () => Record<string, string>;
}

export function createApiClient(config: ApiClientConfig) {
  async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(init.headers as Record<string, string>),
    };

    if (config.getClientHeaders) Object.assign(headers, config.getClientHeaders());

    if (config.getAuthHeader) {
      const authHeader = await config.getAuthHeader();
      if (authHeader) headers["Authorization"] = authHeader;
    }

    const controller = config.timeoutMs ? new AbortController() : null;
    const timer = controller
      ? setTimeout(() => controller.abort(), config.timeoutMs)
      : null;
    let res: Response;
    try {
      res = await fetch(`${config.baseUrl}${path}`, {
        ...init,
        credentials: config.credentials,
        headers,
        ...(controller ? { signal: controller.signal } : {}),
      });
    } catch (err) {
      const timedOut = controller?.signal.aborted === true;
      throw new NetworkError(
        timedOut ? ERROR_COPY.timeout : ERROR_COPY.network,
        timedOut,
        err,
      );
    } finally {
      if (timer) clearTimeout(timer);
    }

    if (!res.ok) {
      let message = "Request failed";
      let errorBody: Record<string, unknown> | undefined;
      try {
        errorBody = (await res.json()) as Record<string, unknown>;
        if (typeof errorBody["error"] === "string") message = errorBody["error"];
      } catch {
        // ignore parse error
      }
      throw new ApiError(res.status, message, errorBody, retryAfterSeconds(res));
    }

    return res.json() as Promise<T>;
  }

  return {
    get: <T>(path: string): Promise<T> => request<T>(path),
    post: <T>(path: string, body?: unknown): Promise<T> =>
      request<T>(path, {
        method: "POST",
        body: body !== undefined ? JSON.stringify(body) : undefined,
      }),
    patch: <T>(path: string, body?: unknown): Promise<T> =>
      request<T>(path, {
        method: "PATCH",
        body: body !== undefined ? JSON.stringify(body) : undefined,
      }),
    put: <T>(path: string, body?: unknown): Promise<T> =>
      request<T>(path, {
        method: "PUT",
        body: body !== undefined ? JSON.stringify(body) : undefined,
      }),
    delete: <T>(path: string, body?: unknown): Promise<T> =>
      request<T>(path, {
        method: "DELETE",
        body: body !== undefined ? JSON.stringify(body) : undefined,
      }),
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
